import React from 'react'
import { AppData, ModuleData } from '../../types/appTypes'
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import "./content.css";
import { Accordion, AccordionTab } from 'primereact/accordion';
import { fetchAppDownloadDetails, fetchApplications } from '../../services/api';

type WebTableViewProps = {
    data: ModuleData[];
};

const WebTableView: React.FC<WebTableViewProps> = ({ data }) => {
    const [appsByModule, setAppsByModule] = React.useState<Record<string, AppData[]>>({});
    const [loadingModule, setLoadingModule] = React.useState<string | null>(null);
    const [activeIndex, setActiveIndex] = React.useState<number | null>(data.length > 0 ? 0 : null);

    // Fetch apps for the first tab on mount if data exists
    React.useEffect(() => {
        if (data.length > 0) {
            handleTabOpen(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const handleTabOpen = async (index: number | null) => {
        setActiveIndex(index);
        if (index === null) return;
        const module = data[index];
        if (!module) return;
        if (appsByModule[module.appId]) return; // already loaded

        setLoadingModule(module.appId);
        try {
            const result = await fetchApplications(Number(module.appId));
            const rawApps = Array.isArray(result) ? result : result.data;
            const apps: AppData[] = rawApps.map((app: any, idx: number) => {
                const moduleData = data.find(m => String(m.appId) === String(app.appId));
                return {
                    id: app.id ?? String(app.appId ?? idx),
                    appName: moduleData?.appName ?? app.appName,
                    os: 'Android',
                    releaseType: app.releaseType,
                    releaseDescription: app.releaseDescription,
                    releaseDate: app.releaseDate,
                    releaseVersion: app.releaseVersion,
                    downloadUrl: app.downloadUrl,
                };
            });
            setAppsByModule(prev => ({ ...prev, [Number(module.appId)]: apps }));
        } finally {
            setLoadingModule(null);
        }
    };

    // Table cell renderers
    const appNameBody = (rowData: AppData) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {(() => {
                const imageDetail = data.find((m: ModuleData) => String(m.appId) === String(rowData.id))?.imageDetail;
                if (imageDetail) {
                    return (
                        <img
                            src={`data:image/png;base64,${imageDetail}`}
                            alt="App Icon"
                            style={{ width: 28, height: 28, objectFit: 'contain' }}
                        />
                    );
                }
                return <i className="pi pi-android" style={{ fontSize: 28, color: '#2D6DF6' }}></i>;
            })()}

            <span>{data.find(m => String(m.appId) === String(rowData.id))?.appName ?? rowData.appName}</span>
        </span>
    );

    const downloadBody = (rowData: AppData) => (
        <Button
            icon="pi pi-download"
            label="Download"
            className="p-button-sm"
            style={{ fontSize: 12 }}
            onClick={async () => {
                try {
                    const response = await fetchAppDownloadDetails(rowData.id, rowData.releaseVersion + '.apk');
                    const url = response;
                    if (url) {
                        const appName =
                            data.find(m => String(m.appId) === String(rowData.id))?.appName
                            ?? rowData.appName
                            ?? "App";
                        const fileName = `${appName}_${rowData.releaseVersion}.apk`;
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = fileName;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }
                } catch (error) {
                    console.error("Download failed", error);
                }
            }}
        />
    );

    const releaseDateBody = (rowData: AppData) => {
        if (!rowData.releaseDate) return '';
        const date = new Date(rowData.releaseDate);
        return date.toLocaleDateString();
    };

    return (
        <Accordion
            activeIndex={activeIndex !== null ? activeIndex : undefined}
            onTabChange={(e) => {
                let index: number | null;
                if (typeof e.index === 'number') {
                    // If clicking the already open tab, close it
                    index = (activeIndex === e.index) ? null : e.index;
                } else if (Array.isArray(e.index)) {
                    index = e.index.length > 0 ? e.index[0] : null;
                } else {
                    index = null;
                }
                handleTabOpen(index);
            }}
        >
            {data.map((module, idx) => (
                <AccordionTab key={module.appId} header={module.appName}>
                    {loadingModule === module.appId ? (
                        <div>Loading...</div>
                    ) : (
                        <div className='p-3'>
                            <DataTable
                                value={appsByModule[module.appId] || []}
                                rows={3}
                                tableStyle={{ minWidth: '50rem' }}
                                className="p-datatable-striped"
                                tableClassName="p-datatable-gridlines custom-bordered-table"
                            >
                                <Column field="appName" header="Application Name" body={appNameBody} />
                                <Column field="os" header="OS" />
                                <Column field="releaseVersion" header="Version" />
                                <Column field="releaseDate" header="Released Date" body={releaseDateBody} />
                                <Column header="Download" body={downloadBody} style={{ width: '10rem' }} />
                            </DataTable>
                        </div>
                    )}
                </AccordionTab>
            ))}
        </Accordion>
    );
};

export default WebTableView
